import axios from "axios";

// The Supplier portal's API client. One place that knows the tenant prefix, so a screen never builds
// a path by hand and cannot accidentally address the other portal's endpoints.

const BASE = process.env.REACT_APP_API_URL ?? "";
const TENANT = "t9";

export type ApiResult = {status: number; data: any};

// Never throws on a non-2xx: every auth response is meaningful (400 is a wrong password, 429 is a
// lockout), so the caller reads status and Code rather than catching.
async function request(method: "GET" | "POST", path: string, body?: any, headers?: any): Promise<ApiResult> {
    try {
        const response = await axios({
            method,
            baseURL: BASE,
            url: `${TENANT}/v1/${path}`,
            headers: headers ?? {},
            data: body,
        });
        return {status: response.status, data: response.data};
    } catch (error: any) {
        if (error?.response) {
            return {status: error.response.status, data: error.response.data};
        }
        // A transport failure has no Code, so screens fall through to their generic message.
        return {status: 0, data: null};
    }
}

// The sign-in POST is itself authorized: this token is what keeps the endpoint from being an open
// door for credential stuffing.
export async function preauthToken(): Promise<string | null> {
    const result = await request("GET", "authenticate?Type=preauth");
    return result.status === 200 ? (result.data?.Token ?? null) : null;
}

export async function signIn(email: string, password: string): Promise<ApiResult> {
    const token = await preauthToken();
    if (!token) return {status: 0, data: null};
    return request("POST", "authenticate", {Email: email, Password: password}, {"X-Cres-AccessToken": token});
}

export async function selectAccount(selectionToken: string, accountUSID: string): Promise<ApiResult> {
    // The selection token goes in the SAME header the preauth token used. This method is
    // CUSTOM-authorized, so a request without X-Cres-AccessToken is rejected at the gateway before it
    // reaches the handler; the token carries a paths claim naming exactly this endpoint.
    return request("POST", "authenticate",
        {AccountUSID: accountUSID},
        {"X-Cres-AccessToken": selectionToken});
}

export async function sendResetCode(email: string): Promise<ApiResult> {
    return request("POST", "forgot_password_send_code", {Email: email});
}

export async function verifyResetCode(key: string, code: string): Promise<ApiResult> {
    return request("POST", "forgot_password_verify_code", {Key: key, Code: code});
}

export async function setNewPassword(key: string, code: string, newPassword: string): Promise<ApiResult> {
    return request("POST", "forgot_password", {Key: key, Code: code, NewPassword: newPassword});
}

// The forced first password change, for a contact signing in on an issued password. The temp token
// from CC-T9PW-INIT is the authorisation -- it is scoped to this endpoint alone -- and a successful
// call returns a full session, so the caller lands signed in rather than back at the form.
export async function changeInitialPassword(tempToken: string, newPassword: string): Promise<ApiResult> {
    return request("POST", "authenticate_change_password",
        {NewPassword: newPassword},
        {"X-Cres-AccessToken": tempToken});
}
